import { downloadTimesheets } from "../../utils/downloadTimesheets";
import { demoTimesheets } from "../../utils/demoTimesheets";
import PageHeading from "../../components/PageHeading";
import { contractDate } from "../../utils/planningDates";
import { inTimesheetWeek } from "../../utils/timesheetFilters";
import { createListSearch } from "../../utils/listSearch";
import { getISOWeek, getISOWeekYear, getISOWeeksInYear, startOfISOWeek, addWeeks, addDays } from "date-fns";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Search, Filter, FileText, CheckCircle2, AlertCircle, XCircle, Send, ChevronLeft, ChevronRight, Download, Activity, History } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import releveHeuresService from "../../services/releveHeuresService";
import { Modal } from "antd";
import { toast } from "react-toastify";

const formatTimesheetDate = value => {
  const french = String(value ?? "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  const date = contractDate(french ? `${french[3]}-${french[2]}-${french[1]}` : value);
  return date ? date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
};

const RelevesHeuresListe = () => {
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [showExamples, setShowExamples] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const selectAllRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [historyReleve, setHistoryReleve] = useState(null);
  const historyKey = `jm-rh-history:${localStorage.getItem("userId") || localStorage.getItem("userEmail") || "session"}:${localStorage.getItem("agencyId") || localStorage.getItem("agencyName") || "agency"}`;
  const [history, setHistory] = useState(() => {
    try { const saved = JSON.parse(localStorage.getItem(historyKey) || "[]"); return Array.isArray(saved) ? saved : []; }
    catch { return []; }
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentWeek, setCurrentWeek] = useState(() => getISOWeek(new Date()));
  const [currentYear, setCurrentYear] = useState(() => getISOWeekYear(new Date()));
  const weekStart = addWeeks(startOfISOWeek(new Date(currentYear, 0, 4)), currentWeek - 1);
  const weekEnd = addDays(weekStart, 6);
  const weekDateOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
  const itemsPerPage = 20;
  const shiftWeek = offset => {
    let next = currentWeek + offset;
    let year = currentYear;
    if (next < 1) { year--; next = getISOWeeksInYear(new Date(year, 6, 1)); }
    else if (next > getISOWeeksInYear(new Date(year, 6, 1))) { year++; next = 1; }
    setCurrentWeek(next); setCurrentYear(year); setCurrentPage(1);
  };


  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    period: "current"
  });
  const [showArchived, setShowArchived] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      setIsLoading(true);
      try {
        const data = await releveHeuresService.getMyReleveHeures();
        const transformed = data.map(item => releveHeuresService.transformToFrontendFormat(item));
        if (active) setTimesheets(transformed);
      } catch (error) {
        if (active) {
          setTimesheets([]);
          toast.error("Impossible de charger les relevés d’heures de vos clients.");
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  // Fonction de filtrage des relevés d'heures
  const examples = useMemo(() => demoTimesheets(currentWeek, currentYear), [currentWeek, currentYear]);
  const displayedTimesheets = useMemo(() => showExamples ? [...timesheets, ...examples] : timesheets, [timesheets, examples, showExamples]);
  const weekRows = useMemo(() => displayedTimesheets.filter(row => inTimesheetWeek(row, currentWeek, currentYear)), [displayedTimesheets, currentWeek, currentYear]);
  const weekTimesheets = useMemo(() => weekRows.filter(createListSearch(filters.search, ["employeeName", "employeePosition", "employeeCompany", "employeeId", "clientName", "contractNumber", "contractReference", "id", "startDate", "endDate"])), [weekRows, filters.search]);
  const filteredTimesheets = weekTimesheets.filter(timesheet => {
    // Filtre par statut, en excluant les "integrated" si showArchived est false
    const statusMatch = showArchived
      ? timesheet.status === 'integrated'
      : (filters.status === "all" ? timesheet.status !== 'integrated' : timesheet.status === filters.status);

    return statusMatch;
  });

  const totalPages = Math.ceil(filteredTimesheets.length / itemsPerPage);
  const paginatedTimesheets = filteredTimesheets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const selectedCount = filteredTimesheets.filter(row => selectedIds.has(row.id)).length;
  const pageSelectedCount = paginatedTimesheets.filter(row => selectedIds.has(row.id)).length;
  const allPageSelected = paginatedTimesheets.length > 0 && pageSelectedCount === paginatedTimesheets.length;
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = pageSelectedCount > 0 && !allPageSelected;
  }, [pageSelectedCount, allPageSelected]);
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filters.search, filters.status, currentWeek, currentYear, showArchived, showExamples]);
  const toggleSelection = id => setSelectedIds(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const togglePageSelection = () => setSelectedIds(current => {
    const next = new Set(current);
    paginatedTimesheets.forEach(row => { if (allPageSelected) next.delete(row.id); else next.add(row.id); });
    return next;
  });

  const downloadSelected = async (format = "pdf") => {
    if (downloading) return;
    setDownloading(true);
    try {
      const rows = filteredTimesheets.filter(row => selectedIds.has(row.id));
      await downloadTimesheets(rows, currentWeek, currentYear, format);
      const entry = { date: new Date().toISOString(), action: format === "pdf" ? "PDF groupé préparé" : "ZIP de PDF préparé", week: currentWeek, year: currentYear, rows: rows.map(row => ({ id: row.id, name: row.employeeName, client: row.clientName })) };
      const next = [entry, ...history];
      try { localStorage.setItem(historyKey, JSON.stringify(next)); setHistory(next); }
      catch { toast.warning("Téléchargement préparé, mais impossible d’enregistrer le suivi dans ce navigateur."); }
    } catch {
      toast.error("Impossible de télécharger les relevés sélectionnés.");
    } finally {
      setDownloading(false);
    }
  };

  const prepareReminders = rows => {
    const eligible = rows.filter(row => row.status === "a_saisir");
    if (!eligible.length) return;
    const entry = { date: new Date().toISOString(), action: "Relance à envoyer", week: currentWeek, year: currentYear, rows: eligible.map(row => ({ id: row.id, name: row.employeeName, client: row.clientName })) };
    const next = [entry, ...history];
    try {
      localStorage.setItem(historyKey, JSON.stringify(next));
      setHistory(next);
      toast.info(`${eligible.length} relance(s) préparée(s), à envoyer.`);
    } catch { toast.error("Impossible d’enregistrer les relances."); }
  };

  const historyByReleve = useMemo(() => {
    const index = new Map();
    history.forEach(entry => entry.rows?.forEach(row => {
      if (!index.has(row.id)) index.set(row.id, []);
      index.get(row.id).push(entry);
    }));
    return index;
  }, [history]);

  // Les cartes résument la semaine sélectionnée, indépendamment du filtre de statut.
  const stats = {
    aSaisir: weekTimesheets.filter(row => row.status === "a_saisir").length,
    aValider: weekTimesheets.filter(row => row.status === "a_valider").length,
    integrated: weekTimesheets.filter(row => row.status === "integrated").length
  };

  // Fonction pour naviguer vers la page de détails d'un relevé d'heures
  const viewTimesheet = (id) => {
    navigate(`/RelevesHeures?id=${id}`);
  };

  // Fonction pour obtenir la couleur et l'icône du statut
  const getStatusInfo = (status) => {
    switch(status) {
      case "pending":
        return {
          color: "bg-amber-50 text-amber-700 border-amber-200",
          icon: <Clock className="w-4 h-4" />,
          label: "En attente"
        };
      case "a_saisir":
        return {
          color: "bg-red-50 text-red-700 border-red-200",
          icon: <AlertCircle className="w-4 h-4" />,
          label: "À saisir"
        };
      case "a_valider":
        return {
          color: "bg-orange-50 text-orange-700 border-orange-200",
          icon: <FileText className="w-4 h-4" />,
          label: "À valider"
        };
      case "integrated":
        return {
          color: "bg-emerald-50 text-emerald-700 border-emerald-200",
          icon: <CheckCircle2 className="w-4 h-4" />,
          label: "Intégré"
        };
      default:
        return {
          color: "bg-slate-50 text-slate-700 border-slate-200",
          icon: <XCircle className="w-4 h-4" />,
          label: status
        };
    }
  };
  
  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header Section */}
        <motion.div
          className="mb-6"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div><PageHeading icon="hours" title={<> Relevés d'Heures </>} description={<> Gestion et suivi des feuilles de temps </>} />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <motion.button
                onClick={() => setShowArchived(!showArchived)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  showArchived
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {showArchived ? 'Voir les actifs' : 'Voir les archives'}
              </motion.button>

              <motion.button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Download className="w-4 h-4" />
                <span>Exporter</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <button type="button" onClick={() => { setShowExamples(value => !value); setCurrentPage(1); }} className="rounded-lg border border-amber-300 px-3 py-2 text-sm font-medium text-amber-900">{showExamples ? "Masquer les exemples" : "Afficher les exemples"}</button>
        </div>
        {/* Métriques Principales */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          {/* À Saisir */}
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">À Saisir</p>
                  <p className="text-xs text-slate-500">Action requise</p>
                </div>
              </div>
              <Activity className="w-4 h-4 text-red-500" />
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-slate-900">{stats.aSaisir}</p>
              <p className="text-xs text-slate-500">Relevés à saisir cette semaine</p>
            </div>
          </motion.div>

          {/* À Valider */}
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <FileText className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">À Valider</p>
                  <p className="text-xs text-slate-500">En révision</p>
                </div>
              </div>
              <CheckCircle2 className="w-4 h-4 text-orange-500" />
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-slate-900">{stats.aValider}</p>
              <p className="text-xs text-slate-500">À valider cette semaine</p>
            </div>
          </motion.div>

          {/* Intégrés */}
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">Intégrés</p>
                  <p className="text-xs text-slate-500">Terminés</p>
                </div>
              </div>
              <Activity className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-slate-900">{stats.integrated}</p>
              <p className="text-xs text-slate-500">Intégrés cette semaine</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Filtres et Navigation */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Recherche */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Recherche</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Nom, poste, entreprise..."
                  value={filters.search}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, search: e.target.value }));
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Statut */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Statut</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <select
                  value={filters.status}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, status: e.target.value }));
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                  disabled={showArchived}
                >
                  <option value="all">Tous les statuts</option>
                  <option value="a_saisir">À saisir</option>
                  <option value="a_valider">À valider</option>
                  <option value="integrated">Intégrés</option>
                </select>
              </div>
            </div>

            {/* Navigation semaine */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Semaine</label>
              <div className="flex items-center space-x-2">
                <motion.button
                  className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => shiftWeek(-1)}
                >
                  <ChevronLeft className="w-4 h-4 text-slate-600" />
                </motion.button>

                <div className="flex-1 text-center">
                  <span className="block text-sm font-medium leading-tight text-slate-900">Semaine {currentWeek}/{currentYear}</span>
                  <p className="m-0 text-xs leading-tight text-slate-500" aria-live="polite">
                    Du lundi {weekStart.toLocaleDateString("fr-FR", weekDateOptions)} au dimanche {weekEnd.toLocaleDateString("fr-FR", weekDateOptions)}
                  </p>
                </div>

                <motion.button
                  className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => shiftWeek(1)}
                >
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </motion.button>
              </div>
            </div>

          </div>
        </motion.div>

        {/* Liste des Relevés d'Heures */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              {/* Indicateur de source de données */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-slate-500">
                  {filteredTimesheets.length} relevé(s) pour cette semaine
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-slate-500">
                <span>{filteredTimesheets.length} relevé(s)</span>
              </div>
            </div>
          </div>

          {selectedCount > 0 && <div className="flex flex-wrap items-center justify-between gap-3 border-b border-blue-100 bg-blue-50 px-6 py-3">
            <p className="text-sm font-medium text-blue-900" role="status">{selectedCount} relevé{selectedCount > 1 ? "s" : ""} sélectionné{selectedCount > 1 ? "s" : ""}</p>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => downloadSelected("pdf")} disabled={downloading} className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"><Download size={16} />{downloading ? "Préparation…" : "Télécharger le PDF"}</button>
              <button type="button" onClick={() => downloadSelected("zip")} disabled={downloading} className="rounded-lg border border-blue-200 px-3 py-2 text-sm font-medium text-blue-700 disabled:opacity-50">Télécharger les PDF (ZIP)</button>
              <button type="button" onClick={() => prepareReminders(filteredTimesheets.filter(row => selectedIds.has(row.id)))} disabled={!filteredTimesheets.some(row => selectedIds.has(row.id) && row.status === "a_saisir")} className="inline-flex items-center gap-2 rounded-lg border border-blue-200 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"><Send size={16} />Préparer les relances</button>
              <button type="button" onClick={() => setSelectedIds(new Set())} className="rounded-lg px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100">Tout désélectionner</button>
            </div>
          </div>}
          <div className="overflow-x-auto">
            <table data-pagination="server" className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="w-16 px-6 py-3 text-center" style={{ width: 64 }}>
                    <input ref={selectAllRef} type="checkbox" aria-label="Sélectionner tous les relevés de cette page" checked={allPageSelected} disabled={!paginatedTimesheets.length} onChange={togglePageSelection} className="timesheet-checkbox" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Intérimaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                <AnimatePresence>
                  {filteredTimesheets.length > 0 ? (
                    paginatedTimesheets.map((timesheet, index) => {
                      const statusInfo = getStatusInfo(timesheet.status);
                      return (
                        <motion.tr
                          key={timesheet.id}
                          className={`${selectedIds.has(timesheet.id) ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-slate-50"} cursor-pointer transition-colors duration-200`}
                          onClick={() => timesheet.isDemo ? setHistoryReleve(timesheet) : viewTimesheet(timesheet.id)}
                        >
                          <td className="w-16 px-6 py-4 text-center" style={{ width: 64 }} onClick={event => event.stopPropagation()}>
                            <input type="checkbox" aria-label={`Sélectionner le relevé de ${timesheet.employeeName} du ${formatTimesheetDate(timesheet.startDate)}`} checked={selectedIds.has(timesheet.id)} onChange={() => toggleSelection(timesheet.id)} className="timesheet-checkbox" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-slate-900">{timesheet.employeeName}{timesheet.isDemo && <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Fictif</span>}</div>
                            <div className="text-sm text-slate-500">{timesheet.employeePosition}</div>
                            <div className="text-xs text-slate-400">{timesheet.employeeCompany} • {timesheet.employeeId}</div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900 font-medium">{timesheet.clientName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-500">
                              {formatTimesheetDate(timesheet.startDate)} → {formatTimesheetDate(timesheet.endDate)}
                            </div>
                            <div className="text-xs text-slate-400">Semaine {currentWeek}/{currentYear}</div>
                            <div className="mt-1 text-xs font-medium text-slate-600">Contrat n° {timesheet.contractNumber !== "" && timesheet.contractNumber != null ? `${timesheet.contractNumber}${timesheet.amendmentNumber !== "" && timesheet.amendmentNumber != null ? `.${timesheet.amendmentNumber}` : ""}` : "—"}</div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusInfo.color}`}>
                              {statusInfo.icon}
                              <span className="ml-1">{statusInfo.label}</span>
                            </span>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                            <div className="flex items-center justify-center space-x-2">
                              <button type="button" onClick={event => { event.stopPropagation(); setHistoryReleve(timesheet); }} title="Consulter le suivi du RH" aria-label={`Consulter le suivi du RH de ${timesheet.employeeName}`} className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"><History className="h-4 w-4" /></button>
                              {timesheet.status === "a_saisir" && <button type="button" onClick={event => { event.stopPropagation(); prepareReminders([timesheet]); }} title="Préparer une relance" className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"><Send className="w-4 h-4" /></button>}


                            </div>
                          </td>
                        </motion.tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <FileText className="w-12 h-12 text-slate-300 mb-4" />
                          <h3 className="text-sm font-medium text-slate-900 mb-1">{isLoading ? "Chargement des relevés…" : "Aucun relevé trouvé"}</h3>
                          <p className="text-sm text-slate-500">
                            {isLoading ? "Récupération des relevés d’heures de vos clients en cours." : "Aucun relevé d’heures ne correspond à vos critères de recherche."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </motion.div>
        <Modal open={Boolean(historyReleve)} onCancel={() => setHistoryReleve(null)} footer={null} centered title={`Suivi du RH — ${historyReleve?.employeeName || ""}`}>
          {historyReleve && <>
            <p className="mb-4 text-sm text-slate-600">{historyReleve.clientName} · {formatTimesheetDate(historyReleve.startDate)} → {formatTimesheetDate(historyReleve.endDate)}</p>
            {(historyByReleve.get(historyReleve.id) || []).length ? <ol className="space-y-3">{historyByReleve.get(historyReleve.id).map((entry, index) => <li key={`${entry.date}-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-800">{entry.action}</p>
              <time dateTime={entry.date} className="mt-1 block text-xs text-slate-500">{new Date(entry.date).toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</time>
            </li>)}</ol> : <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Aucune action enregistrée pour ce RH.</p>}
            <p className="mt-4 text-xs text-slate-500">Historique local à ce navigateur. Les relances préparées restent à envoyer.</p>
          </>}
        </Modal>
        {/* Pagination */}
        {totalPages > 1 && (
          <motion.div
            className="flex justify-between items-center mt-8 px-6 py-4 bg-white rounded-xl shadow-sm border border-slate-200"
          >
            <div className="flex items-center space-x-2 text-sm text-slate-600">
              <span>Page {currentPage} sur {totalPages}</span>
              <span>•</span>
              <span>{paginatedTimesheets.length} sur {filteredTimesheets.length} relevés</span>
            </div>

            <div className="flex items-center space-x-2">
              <motion.button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                whileHover={{ scale: currentPage === 1 ? 1 : 1.05 }}
                whileTap={{ scale: currentPage === 1 ? 1 : 0.95 }}
              >
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </motion.button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <motion.button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {pageNum}
                    </motion.button>
                  );
                })}
              </div>

              <motion.button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                whileHover={{ scale: currentPage === totalPages ? 1 : 1.05 }}
                whileTap={{ scale: currentPage === totalPages ? 1 : 0.95 }}
              >
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RelevesHeuresListe;